import type { FormField } from '../../types';
import { matchesExportAnswers, type AnswerFilter } from '../campaign-export-filters';
import type { CampaignOverviewModel } from './overview';

/** A clicked mark: a category, a histogram bar, or a cross-tab cell. */
export type DrillTarget =
  | { type: 'category'; fieldId: string; key: string }
  | { type: 'range'; fieldId: string; x0: number; x1: number; closed: boolean }
  | { type: 'cell'; a: { fieldId: string; key: string }; b: { fieldId: string; key: string } };

/** Respondents behind a mark, in population order, and the export conditions that select them when expressible. */
export interface DrillResult {
  ids: string[];
  filters: AnswerFilter[];
}

/** Export condition for one category, when the export vocabulary can express it. */
function categoryFilter(fields: FormField[], fieldId: string, key: string): AnswerFilter | null {
  const definition = fields.find(item => item.id === fieldId);
  if (!definition) return null;
  if (definition.type === 'checkbox') return { fieldId, operator: 'eq', value: key === 'true' };
  if (definition.type === 'choice_single') return { fieldId, operator: 'eq', value: key };
  if (definition.type === 'choice_multi' || definition.type === 'image_choice') return { fieldId, operator: 'contains', value: key };
  return null;
}

/** Respondents in one category: through the export matcher when expressible, else from the encoding. */
function categoryIds(model: CampaignOverviewModel, fields: FormField[], fieldId: string, key: string): DrillResult {
  const filter = categoryFilter(fields, fieldId, key);
  if (filter) {
    return { ids: model.population.filter(row => matchesExportAnswers(row, fields, { answers: [filter] })).map(row => row.id), filters: [filter] };
  }
  const encoding = model.encoded.get(fieldId)?.encoding;
  if (encoding?.type !== 'categorical') return { ids: [], filters: [] };
  return { ids: model.population.filter(row => encoding.byResponse.get(row.id)?.includes(key)).map(row => row.id), filters: [] };
}

/**
 * The respondents behind a clicked bar, slice or cell, so the response list
 * can open filtered to exactly the people the mark counts.
 */
export function drillDown(model: CampaignOverviewModel, fields: FormField[], target: DrillTarget): DrillResult {
  if (target.type === 'category') return categoryIds(model, fields, target.fieldId, target.key);
  if (target.type === 'cell') {
    const a = categoryIds(model, fields, target.a.fieldId, target.a.key);
    const b = new Set(categoryIds(model, fields, target.b.fieldId, target.b.key).ids);
    return { ids: a.ids.filter(item => b.has(item)), filters: [] };
  }
  const encoding = model.encoded.get(target.fieldId)?.encoding;
  if (encoding?.type !== 'numeric') return { ids: [], filters: [] };
  const inside = (value: number) => value >= target.x0 && (target.closed ? value <= target.x1 : value < target.x1);
  const ids = model.population.filter(row => {
    const value = encoding.byResponse.get(row.id);
    return value !== undefined && inside(value);
  }).map(row => row.id);
  const numberField = fields.find(item => item.id === target.fieldId)?.type === 'number';
  const filters: AnswerFilter[] = !numberField ? []
    : target.x0 === target.x1 ? [{ fieldId: target.fieldId, operator: 'eq', value: target.x0 }]
      : [{ fieldId: target.fieldId, operator: 'gte', value: target.x0 }, { fieldId: target.fieldId, operator: target.closed ? 'lte' : 'lt', value: target.x1 }];
  return { ids, filters };
}
